import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import EmailVerificationScreen from "./EmailVerificationScreen";
import { VocreaLogo } from "./VocreaLogo";

interface LoginScreenProps {
  onSwitchToRegister: () => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onSwitchToRegister }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [validationErrors, setValidationErrors] = useState({
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const { state, login } = useApp();

  // Form validation
  const validateForm = () => {
    const errors = {
      email: "",
      password: "",
    };

    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    setValidationErrors(errors);
    return !errors.email && !errors.password;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Real-time validation
    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await login(formData.email, formData.password);
    } catch (error: any) {
      // Check if error is due to unverified email
      if (error?.message?.includes("Email not verified") ||
          error?.message?.includes("not verified") ||
          state.error?.includes("Email not verified")) {
        setVerificationEmail(formData.email);
        setShowVerification(true);
      }
      // Other errors handled by context
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationComplete = async (token: string) => {
    // Store token and refresh user info
    localStorage.setItem("token", token);
    setShowVerification(false);
    // Reload to update state
    window.location.reload();
  };

  const handleResendCode = () => {
    console.log("Resending verification code to:", verificationEmail);
  };

  const handleBackFromVerification = () => {
    setShowVerification(false);
    setVerificationEmail("");
  };

  // Social login handler
  const handleSocialLogin = (provider: 'google' | 'facebook' | 'apple') => {
    const apiBaseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    // Redirect to OAuth provider
    window.location.href = `${apiBaseURL}/auth/${provider}`;
  };

  // Keyboard support
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isLoading) {
      handleSubmit(e);
    }
  };

  // Show verification screen if needed
  if (showVerification) {
    return (
      <EmailVerificationScreen
        email={verificationEmail}
        onVerified={handleVerificationComplete}
        onResend={handleResendCode}
        onBack={handleBackFromVerification}
      />
    );
  }

  return (
    <div className="login-screen-modern" role="main">
      {/* Left Side - Login Form */}
      <div className="login-form-side">
        <div className="login-form-container">
          {/* Logo and Header */}
          <div className="login-header">
            <div className="logo-container">
              <VocreaLogo />
              <h1 className="logo-text">Vocrea AI</h1>
            </div>
            <h2 className="welcome-title">Welcome back</h2>
            <p className="welcome-subtitle">Sign in to your account to continue</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="auth-form" noValidate>
            <div className="form-field">
              <label htmlFor="email" className="field-label">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                className={`field-input ${validationErrors.email ? "input-error" : ""}`}
                placeholder="you@example.com"
                disabled={isLoading}
                aria-invalid={!!validationErrors.email}
                aria-describedby={
                  validationErrors.email ? "email-error" : undefined
                }
                required
              />
              {validationErrors.email && (
                <span id="email-error" className="field-error" role="alert">
                  {validationErrors.email}
                </span>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="password" className="field-label">
                Password
              </label>
              <div className="password-input-wrapper">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  onKeyPress={handleKeyPress}
                  className={`field-input ${
                    validationErrors.password ? "input-error" : ""
                  }`}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  aria-invalid={!!validationErrors.password}
                  aria-describedby={
                    validationErrors.password ? "password-error" : undefined
                  }
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {validationErrors.password && (
                <span id="password-error" className="field-error" role="alert">
                  {validationErrors.password}
                </span>
              )}
            </div>

            <div className="form-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="checkbox-input"
                />
                <span>Remember me</span>
              </label>
              <a href="#forgot-password" className="forgot-link">
                Forgot password?
              </a>
            </div>

            {state.error && (
              <div className="auth-error" role="alert">
                {state.error}
              </div>
            )}

            <button
              type="submit"
              className={`auth-submit-button ${isLoading ? "button-loading" : ""}`}
              disabled={isLoading || !formData.email || !formData.password}
              aria-busy={isLoading}
            >
              {isLoading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          {/* Social Login Separator */}
          <div className="social-login-separator">
            <span>Or continue with</span>
          </div>

          {/* Social Login Buttons */}
          <div className="social-login-buttons">
            <button
              type="button"
              onClick={() => handleSocialLogin('google')}
              className="social-login-btn google-btn"
              disabled={isLoading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Google
            </button>

            <button
              type="button"
              onClick={() => handleSocialLogin('facebook')}
              className="social-login-btn facebook-btn"
              disabled={isLoading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Facebook
            </button>

            <button
              type="button"
              onClick={() => handleSocialLogin('apple')}
              className="social-login-btn apple-btn"
              disabled={isLoading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              Apple
            </button>
          </div>

          {/* Sign Up Link */}
          <p className="signup-prompt">
            Don't have an account?{" "}
            <button
              onClick={onSwitchToRegister}
              disabled={isLoading}
              type="button"
              className="signup-link"
            >
              Sign up
            </button>
          </p>
        </div>
      </div>

      {/* Right Side - Marketing Content */}
      <div className="login-marketing-side">
        <div className="marketing-overlay" />
        <div className="marketing-content">
          <h2 className="marketing-title">Emotional support, accessible and on demand</h2>
          <div className="marketing-description">
            <p>
              Vocrea AI connects you to real professionals through a voice-first experience.
            </p>
            <p>
              Our thoughtful LLM assistant listens, matches, and supports you every step of the way.
            </p>
          </div>
          <div className="marketing-stats">
            <div className="stat-card">
              <div className="stat-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">24/7</div>
                <div className="stat-label">Available</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">Real</div>
                <div className="stat-label">Professionals</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                  <line x1="12" y1="19" x2="12" y2="23"></line>
                  <line x1="8" y1="23" x2="16" y2="23"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">Voice</div>
                <div className="stat-label">First</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
